// What a role's interviews ask, per topic, until Caramel has company data.
// `weight`: how often it comes up (0 to 1). `bar`: the skill score that's
// enough for it (0 to 1, same scale as user skills). Topics that come up more
// get a higher bar, so the plan spends more time getting good at them.

import type { CodingTopicId, TopicId } from "./topics";

export type PrepLevel = "intern" | "newgrad" | "mid" | "senior";

export type TopicTarget = { weight: number; bar: number };

export type RoleProfile = Partial<Record<TopicId, TopicTarget>>;

/**
 * How important each topic is in coding interviews, out of 10. The listed
 * ones are LeetCode's DSA course author's ranking (arrays and strings
 * patterns = two pointers and sliding window; intervals are mostly sort and
 * greedy). The unlisted ones (tries, advanced graphs, math, bits) are our
 * guesses, kept low.
 */
const IMPORTANCE: Record<CodingTopicId, number> = {
  trees: 11,
  graphs: 11,
  "arrays-hashing": 10,
  heap: 8.5,
  "binary-search": 8,
  "two-pointers": 7.5,
  "sliding-window": 7.5,
  greedy: 6.5,
  intervals: 6,
  stack: 6,
  backtracking: 5,
  "dp-1d": 4,
  "dp-2d": 4,
  "linked-list": 3,
  tries: 3,
  "advanced-graphs": 3,
  "math-geometry": 2.5,
  "bit-manipulation": 2,
};

/** Intern interviews ask the harder topics less */
const INTERN_FACTOR: Partial<Record<CodingTopicId, number>> = {
  "dp-1d": 0.6,
  "dp-2d": 0.5,
  "advanced-graphs": 0.5,
  tries: 0.6,
  backtracking: 0.8,
};

/** Senior interviews lean on the harder topics a little more */
const SENIOR_FACTOR: Partial<Record<CodingTopicId, number>> = {
  "dp-1d": 1.2,
  "dp-2d": 1.2,
  "advanced-graphs": 1.3,
  tries: 1.2,
  backtracking: 1.1,
};

const MAX_IMPORTANCE = 11;

/** The bar for a topic of weight w: base + slope × w. Higher levels need more of everything. */
const BAR: Record<PrepLevel, { base: number; slope: number }> = {
  intern: { base: 0.35, slope: 0.3 },
  newgrad: { base: 0.4, slope: 0.35 },
  mid: { base: 0.45, slope: 0.35 },
  senior: { base: 0.5, slope: 0.35 },
};

function codingProfile(level: PrepLevel): RoleProfile {
  const profile: RoleProfile = {};
  const factors = level === "intern" ? INTERN_FACTOR : level === "senior" ? SENIOR_FACTOR : {};
  for (const [topic, importance] of Object.entries(IMPORTANCE) as [CodingTopicId, number][]) {
    const weight = round(Math.min(1, (importance / MAX_IMPORTANCE) * (factors[topic] ?? 1)));
    const bar = BAR[level].base + BAR[level].slope * weight;
    profile[topic] = { weight, bar: round(bar) };
  }
  return profile;
}

const round = (n: number) => Math.round(n * 100) / 100;

const t = (weight: number, bar: number): TopicTarget => ({ weight, bar });

export const ROLE_PROFILES: Record<PrepLevel, RoleProfile> = {
  intern: {
    ...codingProfile("intern"),
    "story-conflict": t(0.5, 0.6),
    "story-failure": t(0.4, 0.6),
    "story-leadership": t(0.35, 0.6),
    "story-impact": t(0.45, 0.6),
    "story-teamwork": t(0.45, 0.6),
  },
  newgrad: {
    ...codingProfile("newgrad"),
    "story-conflict": t(0.55, 0.65),
    "story-failure": t(0.45, 0.65),
    "story-leadership": t(0.45, 0.65),
    "story-impact": t(0.5, 0.65),
    "story-teamwork": t(0.45, 0.65),
  },
  mid: {
    ...codingProfile("mid"),
    "story-conflict": t(0.6, 0.7),
    "story-failure": t(0.5, 0.7),
    "story-leadership": t(0.55, 0.7),
    "story-impact": t(0.6, 0.7),
    "story-teamwork": t(0.5, 0.7),
  },
  senior: {
    ...codingProfile("senior"),
    "story-conflict": t(0.65, 0.75),
    "story-failure": t(0.55, 0.75),
    "story-leadership": t(0.7, 0.75),
    "story-impact": t(0.7, 0.75),
    "story-teamwork": t(0.5, 0.75),
  },
};
